/**
 * @fileoverview Shared Color Theme preset picker for the document builders. Renders the
 * THEME_PRESETS swatches and reports the selected preset id. Used by both tenant-web and
 * system-web so the picker can no longer diverge (G-THEME-SW-01, G-PALETTE-01).
 * @module ui-admin/template-builder/documentSectionEditors/ColorThemePicker
 *
 * Responsibilities: - Render the theme-preset swatch grid + selected-label caption.
 * Dependencies:     - @firesquirrel/document-render (THEME_PRESETS, ThemePresetId).
 * Architecture:     - Presentation-only, controlled (value + onChange). No surrounding chrome
 *                     (the page supplies its own AccordionSection wrapper).
 * Notes:            - Extracted from the tenant builder page; system-web previously lacked it entirely.
 */

import { THEME_PRESETS, type ThemePresetId } from '@firesquirrel/document-render';

/**
 * @component ColorThemePicker
 * Purpose:     Pick a named color-theme preset for the document.
 * Props:       value (current ThemePresetId | undefined), onChange(id), disabled?.
 * Behavior:    Controlled; clicking a swatch calls onChange with that preset id.
 * Data Source: local (THEME_PRESETS is static data from document-render).
 * Constraints: presentation-only; no API calls.
 * Performance: trivial (renders THEME_PRESETS swatches).
 */
export function ColorThemePicker({
  value,
  onChange,
  disabled = false,
}: {
  value: ThemePresetId | undefined;
  onChange: (id: ThemePresetId) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <div className="text-xs font-medium text-gray-600 mb-2">Color Theme</div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {THEME_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(preset.id)}
            title={preset.label}
            className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${value === preset.id ? 'border-gray-800 scale-110' : 'border-transparent'}`}
            style={{
              backgroundColor: preset.primary || '#e5e7eb',
              outline: value === preset.id ? '2px solid #374151' : 'none',
              outlineOffset: '1px',
            }}
          />
        ))}
      </div>
      <p className="text-[10px] text-gray-400">
        {THEME_PRESETS.find((p) => p.id === (value ?? 'none'))?.label ?? 'Department Default'}
      </p>
    </div>
  );
}
